import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../auth/AuthContext.js";
import { api, ApiError } from "../../../api/client.js";
import { AddAccountsForm, DeepResearchBankSelector } from "./DeepResearchAccounts.js";
import { DeepResearchBoard } from "./DeepResearchBoard.js";
import { DeepResearchFindAccounts } from "./DeepResearchFindAccounts.js";
import { DeepResearchSelection } from "./DeepResearchSelection.js";
import type { DrBanks, DrJob, DrJobKind, DrStatus, V4Bank } from "./deep-research-api.js";

/**
 * FIX-062 DEEP RESEARCH: V4 (0-1Mv4 Client Kit) inside Emma OS. Bank (IMPORTANT default | ORIGIN) -> V4's commands with every option and its V4 default ->
 * jobs (console lines as V4 prints them, calls, quota left, Gemini cost) -> board -> daily-routine Steps 2-3 (shortlist, pre-screen, 5 + 2 recommendation,
 * the user's 5) -> per pick an RP or RE handoff. Spending actions are admin-only (the server enforces it).
 */
export function DeepResearchTab({ tenantId }: { readonly tenantId: string }) {
  const { csrfToken } = useAuth();
  const base = `/research/v4/${encodeURIComponent(tenantId)}`;
  const [status, setStatus] = useState<DrStatus | null>(null);
  const [banks, setBanks] = useState<DrBanks | null>(null);
  const [bank, setBank] = useState<V4Bank>("IMPORTANT");
  const [jobs, setJobs] = useState<DrJob[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [boardKey, setBoardKey] = useState(0);
  const prevStatus = useRef<Map<string, DrJob["status"]> | null>(null);

  useEffect(() => {
    api.get<DrStatus>(`${base}/status`).then(setStatus).catch(() => setError("Could not load Deep Research."));
    api.get<DrBanks>(`${base}/banks`).then((b) => { setBanks(b); setBank(b.default); }).catch(() => setError("Could not load the banks."));
  }, [base]);

  const loadJobs = useCallback(async () => {
    try {
      const r = await api.get<{ jobs: DrJob[] }>(`${base}/jobs?limit=10`);
      setJobs(r.jobs);
      // FIX-062 live fix 4: any job that finished since the last poll (also one queued and finished between two polls, and the first job ever)
      // refreshes the board, the banks and the shortlist (US column, pre-screen status). The first poll after opening the page only records.
      const prev = prevStatus.current;
      const active = (s: DrJob["status"] | undefined): boolean => s === "queued" || s === "running";
      const finished = prev !== null && r.jobs.some((j) => !active(j.status) && (active(prev.get(j.id)) || !prev.has(j.id)));
      prevStatus.current = new Map(r.jobs.map((j) => [j.id, j.status]));
      if (finished) { setBoardKey((k) => k + 1); api.get<DrBanks>(`${base}/banks`).then(setBanks).catch(() => undefined); }
    } catch { /* the next poll retries */ }
  }, [base]);
  useEffect(() => {
    void loadJobs();
    const t = setInterval(() => void loadJobs(), 3000);
    return () => clearInterval(t);
  }, [loadJobs]);

  const start = async (kind: DrJobKind, params: Record<string, unknown>, jobBank: V4Bank = bank): Promise<void> => {
    setError(null);
    try {
      if (kind === "prescreen") await api.post(`${base}/prescreen`, csrfToken, { bank: jobBank, ...params });
      else await api.post(`${base}/jobs`, csrfToken, { kind, bank: jobBank, params });
      await loadJobs();
    } catch (e) {
      const b = e instanceof ApiError ? (e.body as { error?: string; message?: string } | null) : null;
      setError(b?.message ?? (b?.error === "admin_only" ? "Only an admin can run this." : "Could not start the job."));
    }
  };

  if (!status) return <div><h2>Deep Research</h2>{error ? <p className="error">{error}</p> : <p className="muted">Loading…</p>}</div>;
  return (
    <div data-testid="deep-research">
      <h2>Deep Research</h2>
      <p className="muted">The V4 method, unchanged: track accounts, pull new Reels, check the US score, shortlist, pre-screen, then choose your 5. Times are {status.timeZone}.</p>
      {error ? <p className="error" role="alert">{error}</p> : null}
      <DeepResearchBankSelector banks={banks} bank={bank} onBank={setBank} />
      <section className="card" data-testid="dr-actions">
        <h3>Actions</h3>
        {!status.isAdmin ? <p className="muted">Running these needs an admin.</p> : null}
        {!status.rapidapiConfigured ? <p className="muted">RAPIDAPI_KEY is not configured: Add and Pull are off.</p> : null}
        {!status.geminiConfigured ? <p className="muted">GEMINI_API_KEY is not configured: Score is off.</p> : null}
        <AddAccountsForm status={status} banks={banks} bank={bank} onStart={(p) => start("add_account", p)} />
        <PullForm status={status} onStart={(p) => start("pull_new", p)} />
        <UsCheckForm status={status} base={base} bank={bank} csrfToken={csrfToken} onStart={(p) => start("us_check", p)} />
        <div className="card">
          <h4>Other V4 settings</h4>
          <p className="muted">RAPIDAPI_MAX_PER_MIN (calls per minute, all jobs together): {status.defaults.rapidapiMaxPerMin} (server setting). “Standing OK” daily pull without asking: off (manual, as in V4 by default). The board is always live (V4 --no-board does not apply).</p>
        </div>
      </section>
      {/* V4 7.5 find-accounts (owner t696u): candidates only; "add to bank" = the existing add_account job of the chosen bank. */}
      <DeepResearchFindAccounts status={status} base={base} csrfToken={csrfToken} banks={banks} jobs={jobs}
        onRun={(b, p) => start("find_accounts", p, b)} onAdd={(b, h) => start("add_account", { handles: [h] }, b)} />
      <JobsPanel jobs={jobs} />
      <DeepResearchBoard tenantId={tenantId} bank={bank} refreshKey={boardKey} />
      <DeepResearchSelection tenantId={tenantId} base={base} bank={bank} status={status} csrfToken={csrfToken} jobs={jobs} refreshKey={boardKey} onPrescreen={(p) => start("prescreen", p)} />
    </div>
  );
}

function PullForm({ status, onStart }: { readonly status: DrStatus; readonly onStart: (p: Record<string, unknown>) => Promise<void> }) {
  const [pages, setPages] = useState(status.defaults.pull.pages);
  const [workers, setWorkers] = useState(status.defaults.pull.workers);
  return (
    <div className="card" data-testid="dr-pull">
      <h4>Pull new reels <span className="muted">(pull_new.py)</span></h4>
      <label>Pages per account, 12 reels each (--pages) <input type="number" min={1} value={pages} onChange={(e) => setPages(Number(e.target.value))} /></label>{" "}
      <label>Workers (--workers) <input type="number" min={1} max={8} value={workers} onChange={(e) => setWorkers(Number(e.target.value))} /></label>{" "}
      <button type="button" disabled={!status.isAdmin || !status.rapidapiConfigured} onClick={() => void onStart({ pages, workers })}>Pull</button>
    </div>
  );
}

interface Preview { reels: Array<{ num: number | null; code: string; handle: string; plays: number; duration: number | null; posted: string | null; linkOk: boolean }>; estimateUsd: number; staleLinks: number; lines: string[] }
function UsCheckForm({ status, base, bank, csrfToken, onStart }: { readonly status: DrStatus; readonly base: string; readonly bank: V4Bank; readonly csrfToken: string | null; readonly onStart: (p: Record<string, unknown>) => Promise<void> }) {
  const d = status.defaults.usCheck;
  const [top, setTop] = useState(d.top);
  const [days, setDays] = useState(d.days);
  const [minViews, setMinViews] = useState(d.minViews);
  const [maxLen, setMaxLen] = useState(d.maxLen);
  const [model, setModel] = useState(d.model);
  const [workers, setWorkers] = useState(d.workers);
  const [preview, setPreview] = useState<Preview | null>(null);
  const params = { top, days, minViews, maxLen, model, workers };
  useEffect(() => setPreview(null), [bank, top, days, minViews, maxLen, model, workers]);
  return (
    <div className="card" data-testid="dr-uscheck">
      <h4>US check <span className="muted">(tools/us_check.py, optional)</span></h4>
      <div className="dr-options">
        <label>Top (--top) <input type="number" min={1} value={top} onChange={(e) => setTop(Number(e.target.value))} /></label>
        <label>Days (--days) <input type="number" min={1} value={days} onChange={(e) => setDays(Number(e.target.value))} /></label>
        <label>Min views, 0 = any (--min-views) <input type="number" min={0} value={minViews} onChange={(e) => setMinViews(Number(e.target.value))} /></label>
        <label>Max length s, 30 at most, 0 = 30 (--max-len) <input type="number" min={0} value={maxLen} onChange={(e) => setMaxLen(Number(e.target.value))} /></label>
        <label>Model (--model) <input value={model} onChange={(e) => setModel(e.target.value)} /></label>
        <label>Workers (--workers) <input type="number" min={1} max={4} value={workers} onChange={(e) => setWorkers(Number(e.target.value))} /></label>
      </div>
      <p className="muted">V4's default {status.originalModel} is blocked for this account; {status.model} is the default here.</p>
      <button type="button" onClick={() => void api.post<Preview>(`${base}/us-check/preview`, csrfToken, { bank, params }).then(setPreview).catch(() => setPreview(null))} data-testid="dr-uscheck-preview">Preview</button>{" "}
      <button type="button" disabled={!status.isAdmin || !status.geminiConfigured || !preview || preview.reels.length === 0} onClick={() => void onStart(params)} data-testid="dr-uscheck-score">Score</button>
      {preview ? <pre className="dr-console" data-testid="dr-uscheck-lines">{preview.lines.join("\n")}</pre> : null}
    </div>
  );
}

const KIND_TEXT: Record<DrJobKind, string> = { add_account: "Add accounts", pull_new: "Pull new reels", us_check: "US check", prescreen: "Pre-screen", find_accounts: "Find similar accounts" };
function JobsPanel({ jobs }: { readonly jobs: DrJob[] }) {
  return (
    <section className="card" data-testid="dr-jobs">
      <h3>Jobs</h3>
      {jobs.length === 0 ? <p className="muted">No jobs yet.</p> : null}
      {jobs.map((j) => (
        <details key={j.id} open={j.status === "running" || j.status === "queued"}>
          <summary>
            {KIND_TEXT[j.kind]} · {j.bank} · <strong>{j.status}</strong> · {j.rapidapiCalls} RapidAPI calls{j.quotaRemaining !== null ? ` · ${j.quotaRemaining.toLocaleString("en-US")} left this month` : ""}
            {j.kind === "us_check" || j.kind === "find_accounts" ? ` · Gemini $${j.geminiUsd.toFixed(4)}` : ""} · {new Date(j.createdAt).toLocaleString()}
          </summary>
          <pre className="dr-console">{j.log.join("\n")}</pre>
          {j.error ? <p className="error">{j.error}</p> : null}
        </details>
      ))}
    </section>
  );
}
